import { gql } from '@apollo/client'
import {
	WORKER_ADDRESS_FRAGMENT,
	WORKER_FRAGMENT,
	WORKING_AUTHORIZATION_FRAGMENT,
	WORK_EXPERIENCE_FRAGMENT,
	WORKER_CERTIFICATION_FRAGMENT,
	WORKED_TIMESHEET_FRAGMENT,
	IMAGE_FILE_FRAGMENT,
	SALESFORCE_INTEGRATION_FRAGMENT,
	FILE_FRAGMENT,
	EMERGENCY_CONTACT_FRAGMENT,
	WORKER_CREDENTIAL_FRAGMENT,
	WORK_EXPERIENCE_OVERVIEW_FRAMENT,
	JOB_APPLICANT_FRAGMENT,
	RECRUITER_JOB_FRAGMENT,
	LOCATION_FRAGMENT,
	WORKER_ASSIGNMENT_FRAGMENT,
} from '../apollo.fragment'

export const GET_RECRUITER_CANDIDATE_JOB_MATCHES_QUERY = gql`
	query getRecruiterCandidateJobMatches(
		$workerId: String!
		$sortBy: SortArgument
		$first: Int
		$after: String
		$keyword: String
		$filter: RecruiterCandidateJobMatchFilter
	) {
		recruiterCandidateJobMatches(workerId: $workerId, sortBy: $sortBy, first: $first, after: $after, keyword: $keyword, filter: $filter) {
			totalCount
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				matchingPercentage
				matchingExplanation
				matchingExplanationDetails {
					text
					level
					kind
					rule
					evidenceId
					evidenceIds
				}
				source
				matchedAt
				breakdown {
					rule
					points
					max
					status
					detail
				}
				job {
					id
					title
					externalJobId
					externalUrl
					jobType
					startDate
					duration
					weeklyPayAmount
					minWeeklyPayAmount
					hospital
					jobApplicantCount
					shifts
					location {
						id
						city
						addressState
					}
					account {
						id
						name
					}
				}
			}
		}
	}
`

export const VERIFY_WORKER_QUERY = gql`
	query verifyWorker($token: String!) {
		workerToken(token: $token) {
			token
			worker {
				...workerFragment
				recruiter {
					id
					name
					phone
					email
					title
				}
				company {
					salesforceIntegration {
						...salesforceIntegrationFragment
					}
				}
			}
		}
	}

	${WORKER_FRAGMENT}
	${SALESFORCE_INTEGRATION_FRAGMENT}
`

export const GET_RECRUITER_WORKER_DETAIL_QUERY = gql`
	query getRecruiterWorker($id: String!) {
		recruiterWorker(workerId: $id) {
			...workerFragment
			resume {
				...fileFragment
			}
			workExperienceOverview {
				...workExperienceOverview
			}
			workerAddresses {
				...workerAddressFragment
			}
			workingAuthorization {
				...workingAuthorizationFragment
			}
			workExperiences {
				...workExperienceFragment
			}
			workerCertifications {
				...workerCertificationFragment
			}
			emergencyContact {
				...emergencyContactFragment
			}
		}
	}

	${WORKER_FRAGMENT}
	${WORKER_ADDRESS_FRAGMENT}

	${WORK_EXPERIENCE_FRAGMENT}
	${WORKER_CERTIFICATION_FRAGMENT}
	${FILE_FRAGMENT}
	${EMERGENCY_CONTACT_FRAGMENT}
	${WORKING_AUTHORIZATION_FRAGMENT}
	${WORK_EXPERIENCE_OVERVIEW_FRAMENT}
`

export const GET_WORKER_DETAIL_FOR_TIMECARDS_QUERY = gql`
	query getWorkerDetailTimeQuery($id: String!) {
		worker(id: $id) {
			...workerFragment
			company {
				salesforceIntegration {
					...salesforceIntegrationFragment
				}
			}
			workedTimesheets {
				...workedTimesheetFragment
				timesheetImages {
					imageUrls(size: ["100x100", "200x200"])
					...imageFileFragment
				}
			}
		}
	}

	${WORKER_FRAGMENT}
	${SALESFORCE_INTEGRATION_FRAGMENT}
	${WORKED_TIMESHEET_FRAGMENT}
	${IMAGE_FILE_FRAGMENT}
`

export const GET_CREDENTIALING_URL_QUERY = gql`
	query getWorkerDetailCredentialingQuery($id: String!) {
		worker(id: $id) {
			id
			credentialingUrl
		}
	}
`

export const GET_WORKER_CERTIFICATIONS_QUERY = gql`
	query getWorkerCertificationsQuery($id: String!) {
		workerCertifications(workerId: $id) {
			...workerCertificationFragment
			licenseImages {
				...imageFileFragment
			}
		}
	}

	${WORKER_CERTIFICATION_FRAGMENT}
	${IMAGE_FILE_FRAGMENT}
`

export const GET_WORKER_CREDENTIALS_QUERY = gql`
	query getWorkerCredentialsQuery($filter: WorkerCredentialStatusEnum, $after: String, $before: String, $first: Int, $last: Int) {
		workerCredentials(filter: $filter, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...workerCredentialFragment
			}
		}
	}

	${WORKER_CREDENTIAL_FRAGMENT}
`

export const GET_WORKER_SOCIAL_QUERY = gql`
	query getWorkerSocialQuery {
		workerSocialAuthentications {
			providerName
			id
			providerUuid
			email
		}
	}
`

export const RECRUITER_SEARCH_WORKERS_QUERY = gql`
	query recruiterSearchWorkersQuery($keyword: String, $filter: WorkerFilter, $sort: SortArgument, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterSearchWorkers(keyword: $keyword, filter: $filter, sort: $sort, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...workerFragment
			}
		}
	}

	${WORKER_FRAGMENT}
`

export const RECRUITER_WORKER_ON_ASSIGNMENTS_QUERY = gql`
	query recruiterWorkerOnAssignmentsQuery($sort: SortArgument, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterWorkerOnAssignments(sort: $sort, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...workerAssignmentFragment
			}
		}
	}

	${WORKER_ASSIGNMENT_FRAGMENT}
`

export const GET_RECRUITER_JOB_DETAIL_QUERY = gql`
	query getRecruiterJob($id: String!) {
		recruiterJob(id: $id) {
			...recruiterJobFragment
		}
	}

	${RECRUITER_JOB_FRAGMENT}
`

export const GET_RECRUITER_JOB_APPLICANT_ACTIVITIES_QUERY = gql`
	query getRecruiterJobApplicantActivities($trackableType: String!, $workerId: String!, $trackableId: String!) {
		activities(trackableType: $trackableType, workerId: $workerId, trackableId: $trackableId) {
			id
			key
			message
			owner {
				id
				firstName
				lastName
				fullName
			}
			trackable {
				id
				job {
					id
					title
				}
			}
			createdAt
		}
	}
`

export const GET_RECRUITER_JOB_APPLICANT_QUERY = gql`
	query getRecruiterJobApplicant($id: String!) {
		recruiterJobApplicant(id: $id) {
			...jobApplicantFragment
		}
	}

	${JOB_APPLICANT_FRAGMENT}
`

export const GET_RECRUITER_JOB_DETAIL_TITLE_QUERY = gql`
	query getRecruiterJob($id: String!) {
		recruiterJob(id: $id) {
			id
			title
			jobApplicantCount
			createdDate
			status
		}
		recruiterJobMatchSummary(jobId: $id) {
			matchedCandidateCount
			assignedCandidateCount
		}
	}
`

export const GET_RECRUITER_JOB_APPLICANTS_QUERY = gql`
	query recruiterJobApplicantsQuery(
		$workerId: String
		$jobId: String
		$jobApplicantScope: String
		$sort: SortArgument
		$after: String
		$before: String
		$first: Int
		$last: Int
	) {
		recruiterJobApplicants(
			workerId: $workerId
			jobId: $jobId
			jobApplicantScope: $jobApplicantScope
			sort: $sort
			after: $after
			before: $before
			first: $first
			last: $last
		) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...jobApplicantFragment
				interviewDate
				recruiterRejectionReason
			}
		}
	}

	${JOB_APPLICANT_FRAGMENT}
`

export const GET_RECRUITER_JOB_APPLICANT_WORKERS_QUERY = gql`
	query recruiterJobApplicantWorkersQuery(
		$keyword: String
		$filterScope: String
		$filter: WorkerRosterFilter
		$sort: SortArgument
		$after: String
		$before: String
		$first: Int
		$last: Int
	) {
		recruiterJobApplicantWorkers(
			keyword: $keyword
			filterScope: $filterScope
			filter: $filter
			sort: $sort
			after: $after
			before: $before
			first: $first
			last: $last
		) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...workerFragment
			}
		}
	}

	${WORKER_FRAGMENT}
`

export const GET_WORKER_JOB_MATCHES_QUERY = gql`
	query recruiterWorkerJobMatchesQuery($workerId: String!, $sort: SortArgument) {
		recruiterWorkerJobMatches(workerId: $workerId, sort: $sort) {
			id
			title
			matchingPercentage
			jobApplicantCount
			weeklyPayAmount
			minWeeklyPayAmount
			externalJobId
			hospital
			shifts
			externalUrl
			location {
				...locationFragment
			}
		}
	}
	${LOCATION_FRAGMENT}
`

export const GET_RECRUITER_STATISTICS_QUERY = gql`
	query recruiterStatisticsQuery {
		recruiterStatistics {
			activeJobTotal
			availableTravelerPercentage
			availableTravelerTotal
			matchesTotal
			noQualityMatchesPercentage
			noQualityMatchesTotal
			noToasTodayPercentage
			noToasTotal
			preSubmissionTotal
			submissionTotal
			dashboardSubmissionTotal
			workerTotal
		}
	}
`

export const GET_RECRUITER_WORKER_STATISTICS_QUERY = gql`
	query recruiterWorkerStatisticsQuery($workerId: String!) {
		recruiterWorkerStatistics(workerId: $workerId) {
			assignmentTotal
			lastEngagementScore
			lastJobSearchTotal
		}
	}
`
